/**
 * Browser-side error reporting (T-AN-01).
 *
 * Loaded by Next after hydration. Like the analytics module, it stays inert
 * without a DSN — the dashboard's 2s TTI budget (T-PERF-01) leaves no room
 * for a library nobody configured.
 *
 * "Inert" has to mean *not downloaded*, not merely *not initialised*. A
 * static `import * as Sentry from '@sentry/nextjs'` put the whole browser
 * SDK in the first-load bundle on every route whether or not a DSN was
 * configured: 131 KB over the wire, 16% of the landing page's transfer
 * weight, for code that then did nothing. The dynamic import below is the
 * same shape `instrumentation.ts` already uses on the server, and it keeps
 * the SDK out of the graph entirely when `NEXT_PUBLIC_SENTRY_DSN` is unset.
 */

/**
 * Resolves to the initialised SDK, or is `null` when no DSN is configured.
 * Evaluated once at module scope so the chunk is requested as early as it
 * can be useful, and never twice.
 */
const sentry = process.env.NEXT_PUBLIC_SENTRY_DSN
  ? import('@sentry/nextjs').then((Sentry) => {
      Sentry.init({
        dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
        environment: process.env.NEXT_PUBLIC_UNIAP_ENV ?? 'development',
        tracesSampleRate: 0.1,
        // No session replay: it would record the profile form, which is exactly
        // the personal data §8 says to minimise.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        sendDefaultPii: false
      })
      return Sentry
    })
  : null

/**
 * Next calls this synchronously at the start of every client navigation.
 *
 * Without a DSN it returns immediately. With one, the call is forwarded on
 * the microtask that resolves the import; `href` and `navigationType` are
 * captured as arguments, so the span still describes the navigation that
 * actually happened rather than whatever the router moved on to.
 */
export function onRouterTransitionStart(href: string, navigationType: string): void {
  if (!sentry) return
  void sentry.then((Sentry) => Sentry.captureRouterTransitionStart(href, navigationType))
}
